import { getValidationBadgeDetails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/bankAccountValidation.utils.ts';
import { DetailField } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/DetailField.tsx';
import { ValidationBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ValidationBadge.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData';
import { MipInvestorBankAccountDetailRes } from '@dealstack/shared/lib/generated';
import { Flex, SimpleGrid } from '@mantine/core';
import ReactCountryFlag from 'react-country-flag';

interface Props {
	bankAccount: MipInvestorBankAccountDetailRes;
}

/**
 * The fields the provider lookup covers, boxed together and tinted by its outcome, so a
 * reviewer can tell at a glance which values were confirmed against the bank directory
 * and which were merely typed in.
 */
export const ValidatedFieldsSection = ({ bankAccount }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { borderColor: outcomeBorderColor, presentation } = getValidationBadgeDetails(
		bankAccount.validation
	);

	// No badge means nothing has looked at the account, so the box stays a plain divider.
	const borderColor = presentation ? outcomeBorderColor : defaultTheme.v3.colors.gray[4];

	const countryCode = bankAccount.countryCode ?? '';
	const countryName = getCountryNameByCode(countryCode);

	return (
		<Flex
			direction="column"
			gap="0.5rem"
			p="0.5rem"
			style={{ border: `1px solid ${borderColor}`, borderRadius: '0.25rem' }}
		>
			{presentation && (
				<Flex justify="flex-end">
					<ValidationBadge validation={bankAccount.validation} />
				</Flex>
			)}

			<SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="1.5rem" verticalSpacing="1rem">
				<DetailField
					label={tMipManage('bank_accounts.export.bank_name')}
					value={bankAccount.bankName}
				/>
				<DetailField
					label={tMipManage('bank_accounts.export.country')}
					value={
						countryName ? (
							<Flex align="center" gap="0.375rem">
								{countryName}
								<ReactCountryFlag
									countryCode={countryCode}
									svg
									title={countryName}
									aria-label={countryName}
								/>
							</Flex>
						) : null
					}
				/>
				<DetailField
					label={tMipManage('bank_accounts.details.swift_bic')}
					value={bankAccount.identifiers.find(({ key }) => key === 'bic')?.value}
				/>
				<DetailField
					label={tMipManage('bank_accounts.export.currency')}
					value={bankAccount.currency}
				/>
			</SimpleGrid>
		</Flex>
	);
};
